import clsx from 'clsx';
import {
  type ButtonHTMLAttributes,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { BiSolidChevronDown } from 'react-icons/bi';
import { FiMinus, FiPlus } from 'react-icons/fi';

import s from './FormInputs.module.scss';

type NativeInputProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
type NativeButtonProps = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

export const FormInput = ({
  label,
  error,
  mandatory,
  className,
  ...props
}: {
  label?: string;
  error?: string;
  mandatory?: boolean;
} & NativeInputProps) => (
  <label className={clsx(s.InputRoot, className)}>
    {label && (
      <div className={s.Label}>
        <span>{label}</span>
        {mandatory && <span>*</span>}
      </div>
    )}
    <div className={clsx(s.InputWrapper, error && s.error)}>
      <input type="text" {...props} />
    </div>
    {!!error && <p className={s.Error}>{error}</p>}
  </label>
);

type Option<T> = { label: ReactNode; value: T; disabled?: boolean };

export function FormSelect<T extends number | string>({
  label,
  error,
  placeholder,
  options,
  value,
  onChange,
  mandatory,
  disabled,
  className,
}: {
  label: string;
  error?: string;
  placeholder?: string;
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  mandatory?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const [opened, setOpened] = useState(false);
  const ref = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    if (!opened) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpened(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [opened]);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  const select = (option: Option<T>) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpened(false);
  };

  return (
    <label
      className={clsx(s.InputRoot, className)}
      ref={ref}
      onClick={() => !disabled && setOpened((v) => !v)}
    >
      <div className={s.Label}>
        <span>{label}</span>
        {mandatory && <span>*</span>}
      </div>
      <div
        className={clsx(
          s.InputWrapper,
          s.SelectWrapper,
          s.withIcon,
          disabled && s.disabled,
          error && s.error,
          opened && s.Opened,
        )}
      >
        <div className={s.Input}>
          {selectedLabel || <span className={s.Placeholder}>{placeholder}</span>}
        </div>
        <BiSolidChevronDown className={s.icon} />
        {opened && (
          <ul className={s.Popout}>
            {options.map((option) => (
              <li
                key={String(option.value)}
                className={option.disabled ? s.optionDisabled : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  select(option);
                }}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      {!!error && <p className={s.Error}>{error}</p>}
    </label>
  );
}

export const FormButton = ({ children, className, ...props }: NativeButtonProps) => (
  <button type="button" className={clsx(s.Button, className)} {...props}>
    {children}
  </button>
);

export const FormButtonOutlined = ({ className, ...props }: NativeButtonProps) => (
  <FormButton className={clsx(s.OutlinedButton, className)} {...props} />
);

export const FormButtonOutlinedSmall = ({ className, ...props }: NativeButtonProps) => (
  <FormButtonOutlined className={clsx(s.SmallButton, className)} {...props} />
);

export const FormButtonOutlinedError = ({ className, ...props }: NativeButtonProps) => (
  <FormButtonOutlined className={clsx(s.OutlinedErrorButton, className)} {...props} />
);

export const Radio = ({
  checked,
  onChange,
  label,
  className,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  className?: string;
}) => (
  <label className={clsx(s.Radio, className)}>
    <div className={clsx(s.circle, checked && s.checked)} />
    <div>{label}</div>
    <input className={s.input} type="radio" checked={checked} onChange={onChange} />
  </label>
);

/**
 * Integer picker: minus and plus on the sides, the value in the middle. Preferred over
 * a select when the value is an open-ended count rather than a fixed set of choices.
 */
export const NumberStepper = ({
  label,
  value,
  onChange,
  min = 0,
  max,
  mandatory,
  disabled,
  className,
}: {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  /** Omitted means unbounded — nothing is silently capped. */
  max?: number;
  mandatory?: boolean;
  disabled?: boolean;
  className?: string;
}) => {
  const step = (delta: number) =>
    onChange(Math.min(Math.max(value + delta, min), max ?? Number.POSITIVE_INFINITY));

  return (
    <div className={clsx(s.InputRoot, className)}>
      {label && (
        <div className={s.Label}>
          <span>{label}</span>
          {mandatory && <span>*</span>}
        </div>
      )}
      <div className={clsx(s.Stepper, disabled && s.disabled)}>
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={disabled || value <= min}
          aria-label={label ? `Decrease ${label.toLowerCase()}` : 'Decrease'}
        >
          <FiMinus />
        </button>
        <span className={s.StepperValue} aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          onClick={() => step(1)}
          disabled={disabled || (max !== undefined && value >= max)}
          aria-label={label ? `Increase ${label.toLowerCase()}` : 'Increase'}
        >
          <FiPlus />
        </button>
      </div>
    </div>
  );
};

export const FormGroup = ({
  label,
  cols,
  children,
}: {
  label: string;
  cols: number;
  children: ReactNode;
}) => (
  <div className={s.FormGroup}>
    <p className={s.Label}>{label}</p>
    <div className={s.body} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {children}
    </div>
  </div>
);
